import React from 'react';
import styles from './GoogleAnalyticsLogo.module.css';

// Real official Google Analytics brand assets — fetched and staged by the
// Product Manager Agent from Google's own servers (see PRD:
// grant-access-connector-branding, G16 / Appendix Section 2). Never redraw
// or recolor these: the icon is true vector SVG with the exact gold
// (#F9AB00) / orange (#E37400) fills as fetched, and the two lockups are the
// official PNG exports. Rendered as opaque asset references (<img src>),
// never inlined as raw SVG markup and never touched with a CSS
// fill/filter/color override — this is what "colors must never be modified"
// means in practice for a React component (an inlined <svg> could be
// re-styled by an ancestor CSS rule by accident; an <img> physically cannot
// be recolored by CSS).
import googleAnalyticsIconAsset from '../../../assets/icons/google-analytics.svg';
import googleAnalyticsHorizontalAsset from '../../../assets/icons/google-analytics-logo-horizontal.png';
import googleAnalyticsVerticalAsset from '../../../assets/icons/google-analytics-logo-vertical.png';

// Official native pixel dimensions of the staged lockup exports — used only
// to compute an aspect-ratio-correct size when a caller requests a custom
// width/height (see resolveLockupSize below). Not itself a brand rule.
const HORIZONTAL_NATIVE = { width: 1519, height: 402 };
const VERTICAL_NATIVE = { width: 927, height: 388 };

// Published minimum render sizes (PRD G16). A lockup must never be displayed
// smaller than this in either dimension.
const HORIZONTAL_MIN = { width: 154, height: 50 };
const VERTICAL_MIN = { width: 132, height: 100 };

// The icon-only mark has no separately published minimum (G16). 50px is a
// conservative floor borrowed from the horizontal lockup's published minimum
// height, applied here as guidance rather than a hard clamp — the existing
// `/grant-access` connector grid already renders every platform's brand
// mark at 32px side-by-side for visual parity (Shopify, Meta, etc. — see
// GrantOnboardingAccess.jsx), and forcibly growing only the Google Analytics
// tile would break that parity without a documented rule requiring it. Below
// this floor, a one-time dev-console warning is logged so a future misuse
// (e.g. shrinking it well below legible size) doesn't go unnoticed.
const ICON_RECOMMENDED_MIN_SIZE = 50;
let hasWarnedIconSize = false;

// Official Google Analytics destination — the URL callers can pass as
// `href` to make the logo link back to Google Analytics itself (as opposed
// to triggering an in-app action, like the connector tile's own click-to-
// connect behavior does). Exported so consumers don't have to hardcode it.
export const GOOGLE_ANALYTICS_URL = 'https://analytics.google.com/';

// Shared "wrap in a link when asked" behavior, matching this codebase's
// existing external-link convention (see GlobalMessages/MessageTile.jsx's
// CTA link: target="_blank" + rel="noopener noreferrer").
const LinkWrapper = ({ href, label, className, children }) =>
  href ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className={[styles.linkWrapper, className].filter(Boolean).join(' ')}
    >
      {children}
    </a>
  ) : (
    <span className={className}>{children}</span>
  );

/**
 * GoogleAnalyticsIcon — the icon-only Google Analytics mark (three bars +
 * dot), real vector SVG, colors locked.
 *
 * @param {number}  size      - Render size in px, square (default 32, matching
 *                              the existing connector-tile grid's icon size).
 * @param {string}  href      - Optional URL to wrap the mark in a link (e.g.
 *                              GOOGLE_ANALYTICS_URL). Omit for non-link usage
 *                              (e.g. the connect-flow tile, whose click already
 *                              triggers the OAuth flow rather than navigation).
 * @param {string}  className - Optional extra class on the outer wrapper.
 * @param {object}  style     - Optional inline style overrides on the <img>.
 */
export const GoogleAnalyticsIcon = ({ size = 32, href, className, style }) => {
  if (size < ICON_RECOMMENDED_MIN_SIZE && !hasWarnedIconSize && process.env.NODE_ENV !== 'production') {
    hasWarnedIconSize = true;
    // eslint-disable-next-line no-console
    console.warn(
      `GoogleAnalyticsIcon: rendered at ${size}px, below the recommended ${ICON_RECOMMENDED_MIN_SIZE}px legibility floor (PRD grant-access-connector-branding, G16).`
    );
  }

  return (
    <LinkWrapper href={href} label="Google Analytics" className={[styles.markWrapper, className].filter(Boolean).join(' ')}>
      <img
        src={googleAnalyticsIconAsset}
        alt="Google Analytics"
        width={size}
        height={size}
        style={style}
      />
    </LinkWrapper>
  );
};

// Given a lockup's native pixel dimensions and its published minimum, resolve
// a final width/height: honor an explicit width or height (deriving the
// other from the native aspect ratio), default to the native size otherwise,
// then scale up — preserving aspect ratio — just enough to satisfy both
// published minimums simultaneously. This never crops or distorts the
// lockup and never renders it smaller than the published floor in either
// dimension.
const resolveLockupSize = (native, min, requestedWidth, requestedHeight) => {
  const aspect = native.width / native.height;
  let width = requestedWidth;
  let height = requestedHeight;

  if (width && !height) height = width / aspect;
  else if (height && !width) width = height * aspect;
  else if (!width && !height) {
    width = native.width;
    height = native.height;
  }

  const scale = Math.max(min.width / width, min.height / height, 1);
  if (scale > 1) {
    width *= scale;
    height *= scale;
  }
  return { width, height };
};

/**
 * GoogleAnalyticsLogoHorizontal — icon + "Analytics" wordmark lockup.
 * Never rendered below the published minimum of 154×50px.
 *
 * @param {number} width  - Optional explicit width in px.
 * @param {number} height - Optional explicit height in px.
 * @param {string} href   - Optional URL to wrap the lockup in a link.
 */
export const GoogleAnalyticsLogoHorizontal = ({ width, height, href, className, style }) => {
  const size = resolveLockupSize(HORIZONTAL_NATIVE, HORIZONTAL_MIN, width, height);
  return (
    <LinkWrapper href={href} label="Google Analytics" className={[styles.lockupWrapper, className].filter(Boolean).join(' ')}>
      <img
        src={googleAnalyticsHorizontalAsset}
        alt="Google Analytics"
        width={size.width}
        height={size.height}
        style={style}
      />
    </LinkWrapper>
  );
};

/**
 * GoogleAnalyticsLogoVertical — stacked icon + "Google Analytics" wordmark
 * lockup. Never rendered below the published minimum of 132×100px.
 *
 * @param {number} width  - Optional explicit width in px.
 * @param {number} height - Optional explicit height in px.
 * @param {string} href   - Optional URL to wrap the lockup in a link.
 */
export const GoogleAnalyticsLogoVertical = ({ width, height, href, className, style }) => {
  const size = resolveLockupSize(VERTICAL_NATIVE, VERTICAL_MIN, width, height);
  return (
    <LinkWrapper href={href} label="Google Analytics" className={[styles.lockupWrapper, className].filter(Boolean).join(' ')}>
      <img
        src={googleAnalyticsVerticalAsset}
        alt="Google Analytics"
        width={size.width}
        height={size.height}
        style={style}
      />
    </LinkWrapper>
  );
};

// Default export mirrors the icon-only mark — the variant every existing
// connector-tile usage in this codebase needs (see GrantOnboardingAccess.jsx),
// same default-export-is-the-primary-icon convention as components/Icon.
const GoogleAnalyticsLogo = GoogleAnalyticsIcon;

export default GoogleAnalyticsLogo;
